import { LucideIcon } from "lucide-react";
import { NoxusEmptyBlock } from "../../UI/NoxusEmptyBlock";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useSetAtom } from "jotai";
import { showTutorialModalAtom } from "@/store/editor-jotai";
import {
  AssistantsService,
  KbType,
  KnowledgeBasesV2Service,
  UsersService,
  WorkflowService,
} from "@/generated";
import { SpotToast } from "../../SpotToast";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { ReactNode } from "react";
import { NoxusIcon } from "../../shadcn/icons";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import {
  getEmptyFlowDefinition,
  isEditorV2Enabled,
} from "@/utils/emptyFlowDefinition";

const WorkshopEmpty = ({
  toolId,
  icon,
  title,
  button,
  buttonIcon,
  description,
  readerDescription,
  size,
}: {
  toolId: string;
  icon: any;
  title: string;
  button?: string;
  buttonIcon?: LucideIcon | NoxusIcon;
  description: string | ReactNode;
  readerDescription?: string | ReactNode;
  size?: "sm";
}) => {
  const { spotUser, spotGroup } = useSpotUser();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const setShowTutorialModal = useSetAtom(showTutorialModalAtom);
  const editorV2Enabled = isEditorV2Enabled(spotGroup.settings);
  const emptyFlowDefinition = getEmptyFlowDefinition(editorV2Enabled);

  const create_flow_mutation = useMutation({
    mutationFn: WorkflowService.addWorkflow,
    onSuccess: (data, params) => {
      analytics_track(ANALYTICS_EVENTS.WORKFLOW_CREATE, {
        workflow_id: data.data.id,
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
      router.push(`/flows/${data.data.id}`);
    },
    onError: () => {
      SpotToast.error(
        "There was an error creating your Flow. Please try again later."
      );
    },
  });

  const create_flow_mutation_tutorial = useMutation({
    mutationFn: WorkflowService.addWorkflow,
    onSuccess: (data, params) => {
      analytics_track(ANALYTICS_EVENTS.WORKFLOW_CREATE, {
        workflow_id: data.data.id,
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
      setShowTutorialModal(true);
      router.push(`/flows/${data.data.id}`);
    },
    onError: () => {
      SpotToast.error(
        "There was an error creating your Flow. Please try again later."
      );
    },
  });

  const create_kb_mutation = useMutation({
    mutationFn: KnowledgeBasesV2Service.addKnowledgeBaseV2,
    onSuccess: (data) => {
      analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_CREATE, {
        knowledge_base_id: data.data.id,
        kb_type: data.data.kb_type,
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
      });

      router.push(`/data-hub/${data.data.id}`);
    },
    onError: (error) => {
      console.error(error);
      SpotToast.error("There was an error creating your Knowledge Base");
    },
  });

  const create_assistant_mutation = useMutation({
    mutationFn: AssistantsService.addAssistant,
    onSuccess: (data) => {
      analytics_track(ANALYTICS_EVENTS.COWORKER_CREATE, {
        assistant_id: data.data.id,
      });
      router.push(`/assistant/${data.data.id}`);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });
      }, 500);
    },
    onError: () => {
      SpotToast.error("There was an error creating your Agent");
    },
  });

  const update_user_mutation = useMutation({
    mutationFn: UsersService.updateMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.USER] });
    },
  });

  const createNewTool = () => {
    analytics_track(ANALYTICS_EVENTS.WORKFLOW_CREATE, {
      page: pathname,
    });

    if (toolId === "agentic-workflow" && spotUser.settings?.watched_tutorial) {
      create_flow_mutation.mutate({
        path: { group_id: spotGroup.id },
        body: {
          type: "flow",
          name: "Untitled flow",
          definition: emptyFlowDefinition,
        },
      });
    } else if (toolId === "agentic-workflow") {
      update_user_mutation.mutate({
        body: {
          settings: { ...spotUser.settings, watched_tutorial: true },
        },
      });
      create_flow_mutation_tutorial.mutate({
        path: { group_id: spotGroup.id },
        body: {
          type: "flow",
          name: "Untitled flow",
          definition: emptyFlowDefinition,
        },
      });
    } else if (toolId === "co-worker") {
      create_assistant_mutation.mutate({
        path: { group_id: spotGroup.id },
        body: {
          name: "Untitled agent",
        },
      });
    } else if (toolId === "knowledge-base") {
      create_kb_mutation.mutate({
        path: { group_id: spotGroup.id },
        body: {
          name: "Untitled knowledge base",
          description: "",
          kb_type: "entity" as KbType,
          document_types: ["document"],
        },
      });
    }
  };

  const isLoading = () => {
    if (toolId === "agentic-workflow") {
      return (
        create_flow_mutation.status === "pending" ||
        create_flow_mutation_tutorial.status === "pending"
      );
    } else if (toolId === "co-worker") {
      return create_assistant_mutation.status === "pending";
    } else if (toolId === "knowledge-base") {
      return create_kb_mutation.status === "pending";
    }
  };

  return (
    <div className="w-full h-full flex flex-col flex-1 items-center justify-center">
      {hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group") ? (
        <NoxusEmptyBlock
          icon={icon}
          title={title}
          description={description}
          button={button}
          buttonIcon={buttonIcon}
          onClick={createNewTool}
          isLoading={isLoading()}
          size={size}
        />
      ) : (
        <NoxusEmptyBlock
          icon={icon}
          title={title}
          description={readerDescription}
          size={size}
        />
      )}
    </div>
  );
};

export default WorkshopEmpty;
